import type { ReactNode } from 'react';
import { Carousel, CarouselItem } from '@worldcoo/ui/components/carousel';
import { Skeleton } from '@worldcoo/ui/components/skeleton';
import { cn } from '@worldcoo/ui/lib/utils';

/** The inset matches the page's horizontal padding so slides line up with the text above. */
const galleryInset = 'max(var(--gallery-inset), calc((100% - 1366px) / 2 + var(--gallery-inset)))';
const gallerySpacing = 'gap-4 [--gallery-inset:1.25rem] sm:gap-5 sm:[--gallery-inset:2.5rem]';
const galleryPadding = { paddingLeft: galleryInset, paddingRight: galleryInset };
/** Slides per viewport: 1 on mobile, 2 from `sm`, 3 from `lg`. */
const slideWidth = 'w-full basis-[calc(100%-2.5rem)] sm:basis-[calc((100%-1.5rem)/2)] lg:basis-[calc((100%-2.5rem)/3)]';
const slideAspectRatio = 'aspect-[413/286]';

export default function GalleryContent(props: {
	images: { key: string; alt: string; image: ReactNode; mobileOnly?: boolean }[];
}) {
	const hasMultipleImages = props.images.length > 1;

	return (
		<section className="w-full pb-4 sm:pb-6">
			<style>{`@media (min-width: 1024px) { .gallery-carousel::scroll-button(*) { display: none; } }`}</style>
			<Carousel className={cn('gallery-carousel', gallerySpacing)} style={galleryPadding}>
				{props.images.map((item) => (
					<CarouselItem
						key={item.key}
						className={cn(
							slideWidth,
							// Gray surface behind the image. The slides load lazily, so without it the slot is
							// blank until each one decodes; the image covers it exactly once painted.
							'bg-secondary rounded-2xl',
							// With more than one image the mobile slide is narrowed so a sliver of the next one
							// hints that the carousel scrolls; a lone image takes the full width.
							!hasMultipleImages && 'basis-full',
							// The hero image is duplicated here only to gain vertical space on mobile;
							// from lg up it's already shown next to the campaign title in CampaignHero.
							item.mobileOnly && 'lg:hidden',
						)}
					>
						{item.image}
					</CarouselItem>
				))}
			</Carousel>
		</section>
	);
}

/**
 * Placeholder for while the campaign query resolves. A plain flex row rather than a `Carousel`:
 * there is nothing to scroll yet, and it mirrors the real slide widths and aspect ratio so the
 * images drop into place without shifting the page.
 */
export function GallerySkeleton() {
	return (
		<section className="w-full pb-4 sm:pb-6">
			<div className={cn('flex overflow-hidden', gallerySpacing)} style={galleryPadding}>
				{[0, 1, 2].map((index) => (
					<Skeleton key={index} className={cn('bg-secondary shrink-0 rounded-2xl', slideWidth, slideAspectRatio)} />
				))}
			</div>
		</section>
	);
}
